import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getListDocumentsQueryKey } from "~/api/default/default";
import { DocumentStatus, type Document } from "~/api/model";
import { useGetOrganizationSettings } from "~/api/organizations/organizations";
import { customInstance } from "~/custom-instance";
import { useProjectParams } from "~/helpers";
import type {
  ApplyBatesResponse,
  BatesStampingStatus,
  DocumentWithBates,
  FolderWithBates,
  OrganizationSettingsWithBates,
  UpdateBatesStatusResponse,
} from "~/types/bates";
import { useParentCase } from "./useParentCase";

const API_BASE_URL = import.meta.env.VITE_BASE_API_URL;

/**
 * A document can receive a Bates stamp when it is fully processed and not
 * already stamped or explicitly skipped (a detection hint is overridable).
 */
export function isBatesEligible(document: Document): boolean {
  const status = (document as DocumentWithBates).batesStampingStatus;
  return (
    document.status === DocumentStatus.COMPLETE &&
    (!status || status === "NONE" || status === "DETECTED_EXISTING")
  );
}

/**
 * A document is mid-stamping while the restamp lambda re-renders its page
 * images. The pages may still carry pre-stamp overlays during this window, so
 * such a document must not be treated as ready for version/copy flows.
 */
export function isDocumentStamping(document: Document): boolean {
  return (document as DocumentWithBates).batesStampingStatus === "STAMPING";
}

/**
 * Whether Bates numbering applies to the current case: enabled at the
 * organization level and not explicitly disabled on the master case folder
 * (the opt-out is stored on the master case, not on version volumes).
 */
export function useIsBatesEnabled(): boolean {
  const { organizationId } = useProjectParams();
  const { data: settings } = useGetOrganizationSettings(organizationId);
  const { project: masterCase } = useParentCase();

  const orgEnabled =
    (settings as OrganizationSettingsWithBates | undefined)
      ?.batesNumberingEnabled === true;
  const caseDisabled =
    (masterCase as FolderWithBates | undefined)?.batesNumberingDisabled ===
    true;

  return orgEnabled && !caseDisabled;
}

/** Apply Bates numbers to a set of documents. */
export function useApplyBates() {
  const { organizationId, caseId } = useProjectParams();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentIds: string[]) =>
      customInstance<ApplyBatesResponse>({
        url: `${API_BASE_URL}/organizations/${organizationId}/projects/${caseId}/documents/apply-bates`,
        method: "POST",
        data: { documentIds },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getListDocumentsQueryKey(organizationId, caseId),
      });
    },
  });
}

/** Update Bates stamping status (skip / dismiss hint). */
export function useUpdateBatesStatus() {
  const { organizationId, caseId } = useProjectParams();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      documentIds,
      status,
    }: {
      documentIds: string[];
      status: Extract<BatesStampingStatus, "SKIPPED" | "NONE">;
    }) =>
      customInstance<UpdateBatesStatusResponse>({
        url: `${API_BASE_URL}/organizations/${organizationId}/projects/${caseId}/documents/bates-status`,
        method: "POST",
        data: { documentIds, status },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getListDocumentsQueryKey(organizationId, caseId),
      });
    },
  });
}
